import React, { useState } from "react";
import { Descriptions, Button, Space, message, Input, Tag } from "antd";
import { CheckCircle, MessageSquare } from "lucide-react";
import apiService from "../../services/api";
import { formatDateTime } from "../../utils/formatters";

const { TextArea } = Input;

const AlertDetail = ({ alert, onUpdate, onClose }) => {
  const [acknowledging, setAcknowledging] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [comment, setComment] = useState("");

  if (!alert) return null;

  const handleAcknowledge = async () => {
    if (!comment.trim()) {
      message.warning("请输入确认备注");
      return;
    }

    setAcknowledging(true);
    try {
      const response = await apiService.alerts.acknowledgeAlert(alert.id, {
        comment: comment.trim(),
      });
      if (!response.data.success) {
        throw new Error(response.data.message || "确认失败");
      }
      message.success("确认成功");
      onUpdate(response.data.data);
      onClose();
    } catch (err) {
      message.error(err.message || "确认失败");
    } finally {
      setAcknowledging(false);
    }
  };

  const handleResolve = async () => {
    setResolving(true);
    try {
      const response = await apiService.alerts.resolveAlert(alert.id);
      if (!response.data.success) {
        throw new Error(response.data.message || "解决失败");
      }
      message.success("已标记为解决");
      onUpdate(response.data.data);
      onClose();
    } catch (err) {
      message.error(err.message || "解决失败");
    } finally {
      setResolving(false);
    }
  };

  const getSeverityTag = (severity) => {
    const colors = {
      critical: "red",
      warning: "orange",
      info: "blue",
    };
    const labels = {
      critical: "严重",
      warning: "警告",
      info: "信息",
    };
    return <Tag color={colors[severity]}>{labels[severity]}</Tag>;
  };

  const getStatusTag = (status) => {
    const colors = {
      firing: "red",
      acknowledged: "orange",
      resolved: "green",
    };
    const labels = {
      firing: "触发中",
      acknowledged: "已确认",
      resolved: "已解决",
    };
    return <Tag color={colors[status]}>{labels[status]}</Tag>;
  };

  return (
    <div>
      <Descriptions bordered column={1}>
        <Descriptions.Item label="告警标题">{alert.title}</Descriptions.Item>
        <Descriptions.Item label="规则名称">{alert.rule_name}</Descriptions.Item>
        <Descriptions.Item label="严重程度">
          {getSeverityTag(alert.severity)}
        </Descriptions.Item>
        <Descriptions.Item label="账号">{alert.account || '-'}</Descriptions.Item>
        <Descriptions.Item label="交易所">{alert.exchange || '-'}</Descriptions.Item>
        <Descriptions.Item label="状态">
          {getStatusTag(alert.status)}
        </Descriptions.Item>
        <Descriptions.Item label="阅读状态">
          <Tag color={alert.is_read ? "default" : "blue"}>
            {alert.is_read ? "已读" : "未读"}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="告警消息">{alert.message}</Descriptions.Item>
        {alert.data && (
          <Descriptions.Item label="详细数据">
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
              {JSON.stringify(JSON.parse(alert.data), null, 2)}
            </pre>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="触发时间">
          {formatDateTime(alert.fired_at)}
        </Descriptions.Item>
        {alert.resolved_at && (
          <Descriptions.Item label="解决时间">
            {formatDateTime(alert.resolved_at)}
          </Descriptions.Item>
        )}
        {alert.ack_comment && (
          <Descriptions.Item label="确认备注">
            {alert.ack_comment}
          </Descriptions.Item>
        )}
      </Descriptions>

      {alert.status === "firing" && (
        <div style={{ marginTop: 16 }}>
          <TextArea
            rows={3}
            placeholder="请输入确认备注..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <Space>
            <Button
              type="primary"
              icon={<MessageSquare size={16} />}
              loading={acknowledging}
              onClick={handleAcknowledge}
            >
              确认告警
            </Button>
            <Button
              icon={<CheckCircle size={16} />}
              loading={resolving}
              onClick={handleResolve}
            >
              标记为已解决
            </Button>
          </Space>
        </div>
      )}

      {alert.status === "acknowledged" && (
        <div style={{ marginTop: 16 }}>
          <Button
            type="primary"
            icon={<CheckCircle size={16} />}
            loading={resolving}
            onClick={handleResolve}
          >
            标记为已解决
          </Button>
        </div>
      )}
    </div>
  );
};

export default AlertDetail;
